import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, StatusBadge, ColorSwatch, TableRowActions, SortableTh, TableFilterBar, TablePagination } from '@/shared/components'
import { useTypesActivite, useDeleteTypeActivite } from '../hooks/use-types-activite'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useTableSort } from '@/shared/hooks/use-table-sort'
import { usePagination } from '@/shared/hooks/use-pagination'
import { cn } from '@/lib/utils'
import { ConfirmDialog } from '@/shared/components/confirm-dialog'

export function TypeActiviteList() {
  const { t } = useTranslation()
  const { data: types, isLoading } = useTypesActivite()
  const deleteType = useDeleteTypeActivite()
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [statusFilter, setStatusFilter] = useState('all')

  const filtered = useMemo(() => {
    if (!types) return []
    if (statusFilter === 'actif') return types.filter((t) => t.actif)
    if (statusFilter === 'inactif') return types.filter((t) => !t.actif)
    return types
  }, [types, statusFilter])

  const { sortedData, sortKey, sortDirection, handleSort } = useTableSort(filtered, 'libelle')
  const { paginatedData, currentPage, totalPages, totalItems, pageSize, setCurrentPage } = usePagination(sortedData)

  const [deleteDialog, setDeleteDialog] = useState<{ id: number; label: string } | null>(null)

  const handleDelete = (id: number, libelle: string) => {
    setDeleteDialog({ id, label: libelle })
  }

  const handleDeleteConfirm = () => {
    if (deleteDialog) {
      deleteType.mutate(deleteDialog.id)
      setDeleteDialog(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
      </div>
    )
  }

  if (!types || types.length === 0) {
    return <EmptyState message={t('administration.sections.activities.noActivities', "Aucune activité disponible. Créez votre première activité !")} />
  }

  const counts = {
    all: types.length,
    actif: types.filter((t) => t.actif).length,
    inactif: types.filter((t) => !t.actif).length,
  }

  return (
    <>
      <TableFilterBar
        filters={[{
          key: 'status',
          label: 'Statut',
          value: statusFilter,
          options: [
            { value: 'all', label: 'Tous', count: counts.all },
            { value: 'actif', label: 'Actifs', count: counts.actif },
            { value: 'inactif', label: 'Inactifs', count: counts.inactif },
          ],
        }]}
        onFilterChange={(_key, value) => setStatusFilter(value)}
      />
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
        <table className="w-full">
          <thead>
            <tr className="border-b border-neutral-200 bg-gradient-to-r from-primary/5 to-transparent">
              <SortableTh label="Libellé" sortKey="libelle" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Code" sortKey="code" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Couleur" sortKey="couleur" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <SortableTh label="Statut" sortKey="actif" currentSort={sortKey} currentDirection={sortDirection} onSort={handleSort} />
              <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-neutral-400" />
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {paginatedData.map((type) => (
              <tr key={type.id} className={cn('transition-colors hover:bg-neutral-50', !type.actif && 'opacity-50')}>
                <td className="px-4 py-3 text-sm font-medium text-neutral-900">{type.libelle}</td>
                <td className="px-4 py-3"><StatusBadge label={type.code} variant="info" /></td>
                <td className="px-4 py-3"><ColorSwatch color={type.couleur} /></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {type.actif ? <StatusBadge label="Actif" variant="active" /> : <StatusBadge label="Inactif" variant="inactive" />}
                    {type.estSysteme && <StatusBadge label="Système" variant="system" />}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  {(!type.estSysteme || isAdmin) && (
                    <TableRowActions
                      editTo={`/administration/types-activite/${type.id}`}
                      onDelete={() => handleDelete(type.id, type.libelle)}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} pageSize={pageSize} onPageChange={setCurrentPage} />
      </div>

      <ConfirmDialog
        open={!!deleteDialog}
        variant="danger"
        title={`Supprimer l'activité "${deleteDialog?.label}" ?`}
        description="Cette action est irréversible."
        confirmLabel="Supprimer"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteDialog(null)}
      />
    </>
  )
}
